/**
 * Mar 3, 2026 · iOS Add-to-Home-Screen prompt.
 *
 * iOS Safari does not support the standard `beforeinstallprompt` event,
 * so a native mini-banner would never fire. This component fills that
 * gap with a small, one-time instructional banner that only shows to:
 *
 *   • iPhone / iPad Safari users
 *   • who have NOT already dismissed it (localStorage flag)
 *   • who are NOT already in standalone mode (already installed).
 *
 * Two-tap dismiss ("Got it" or the ✕) sets `mes.pwa.ios_prompt_dismissed`
 * to `1` so we don't nag on subsequent visits.
 *
 * ALL detection is wrapped in try/catch — on real iOS Safari `matchMedia`
 * or `navigator.standalone` can throw in weird embedded webviews. A
 * broken banner must never crash the whole app.
 */
import { useEffect, useState } from "react";
import { ShareIcon, PlusSquareIcon, XIcon } from "@phosphor-icons/react";

function isIOS() {
  try {
    if (typeof window === "undefined" || !window.navigator) return false;
    const ua = window.navigator.userAgent || "";
    return /iPhone|iPad|iPod/i.test(ua) || (ua.indexOf("Mac") !== -1 && "ontouchend" in window.document);
  } catch (_e) { return false; }
}
function isStandalone() {
  try {
    if (typeof window === "undefined") return false;
    if (window.navigator && window.navigator.standalone === true) return true;
    if (typeof window.matchMedia === "function") {
      const mq = window.matchMedia("(display-mode: standalone)");
      return !!(mq && mq.matches);
    }
    return false;
  } catch (_e) { return false; }
}

export default function IosInstallBanner() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    try {
      if (!isIOS() || isStandalone()) return;
      if (localStorage.getItem("mes.pwa.ios_prompt_dismissed") === "1") return;
      const t = setTimeout(() => setShow(true), 2000);
      return () => clearTimeout(t);
    } catch (_e) { /* never let the banner take the app down */ }
  }, []);

  const dismiss = () => {
    setShow(false);
    try { localStorage.setItem("mes.pwa.ios_prompt_dismissed", "1"); } catch (_e) { /* ignore */ }
  };

  if (!show) return null;
  return (
    <div
      data-testid="ios-install-banner"
      className="fixed left-3 right-3 bottom-3 sm:left-1/2 sm:right-auto sm:bottom-6 sm:-translate-x-1/2 sm:w-[420px] z-50 rounded-2xl border border-[#007AFF]/40 bg-[var(--bg-1)]/95 backdrop-blur shadow-2xl p-4"
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#007AFF] flex items-center justify-center font-bold text-white text-sm">NF</div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-white text-sm">Install Narrow Fabric</div>
          <div className="text-[12px] text-zinc-300 leading-snug mt-1">
            Tap <ShareIcon size={14} weight="bold" className="inline-block -mt-0.5 mx-0.5 text-[#007AFF]" /> in Safari, then
            <span className="inline-flex items-center gap-1 ml-1 whitespace-nowrap">
              <PlusSquareIcon size={14} weight="bold" className="text-[#007AFF]" />
              <span className="font-medium">Add to Home Screen</span>
            </span>
            .
          </div>
          <button
            data-testid="ios-install-dismiss"
            onClick={dismiss}
            className="mt-2 text-[11px] font-mono uppercase tracking-widest text-[#007AFF] hover:underline"
          >Got it</button>
        </div>
        <button
          data-testid="ios-install-close"
          onClick={dismiss}
          className="text-zinc-500 hover:text-white p-1 -mt-1 -mr-1"
          aria-label="Dismiss install prompt"
        ><XIcon size={14} weight="bold" /></button>
      </div>
    </div>
  );
}
